import { Navigate, Route, Routes } from "react-router-dom";

import { LoginPage } from "@/auth/LoginPage";
import { ForgotPasswordPage } from "@/auth/public/ForgotPasswordPage";
import { ResetPasswordPage } from "@/auth/public/ResetPasswordPage";
import { VerifyEmailPage } from "@/auth/public/VerifyEmailPage";
import { ToolDownloadPage } from "@/auth/public/ToolDownloadPage";
import { useSession } from "@/auth/session";
import { Shell } from "@/app/Shell";
import { PlansModule } from "@/modules/plans/PlansModule";
import { HomePage } from "@/modules/home/HomePage";
import { ConnectModule } from "@/modules/connect/ConnectModule";
import { ScopeProvider } from "@/modules/connect/scope";
import { FotaModule } from "@/modules/fota/FotaModule";
import { AssetDataModule } from "@/modules/asset_data/AssetDataModule";
import { FleetModule } from "@/modules/fleet/FleetModule";
import { LinkModule } from "@/modules/linkops/LinkModule";
import { ApiModule } from "@/modules/api/ApiModule";
import { AssetCatalogModule } from "@/modules/asset_catalog/AssetCatalogModule";
import { OrganisationsModule } from "@/modules/organisations/OrganisationsModule";
import { ProductsModule } from "@/modules/products/ProductsModule";
import { DriversModule } from "@/modules/drivers/DriversModule";
import { RegistryModule } from "@/modules/registry/RegistryModule";

/** The signed-OUT surfaces, reachable with no session and WITHOUT the Shell.
 *
 *  These are the product's on-ramp, and they have to sit OUTSIDE the session
 *  gate to work at all. Before this, `if (!me) return <LoginPage />` swallowed
 *  every path: a person clicking the reset link in their invitation email
 *  landed on the sign-in screen with no way to set the password they were
 *  being invited to choose. Creating a user dead-ended one step after "Send
 *  invite", and the bug was invisible because the login page is a perfectly
 *  reasonable-looking thing to be shown.
 *
 *  They stay mounted when signed IN too — an administrator following their own
 *  tool-download link should get the installer, not a redirect to /home.
 */
const publicRoutes = (
  <>
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/tool-download/:token" element={<ToolDownloadPage />} />
  </>
);

export function App() {
  const { me, loading } = useSession();

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", color: "var(--t-dim)" }}>
        Loading…
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        {publicRoutes}
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <ScopeProvider>
      <Routes>
        {publicRoutes}
        <Route element={<Shell />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/plans/*" element={<PlansModule />} />
          <Route path="/connect/*" element={<ConnectModule />} />
          <Route path="/fota/*" element={<FotaModule />} />
          <Route path="/fleet/*" element={<FleetModule />} />
          <Route path="/asset-data/*" element={<AssetDataModule />} />
          {/* ASSET CONTROL MOVED TO /fleet (its gate is GATE_FLEET, not
              GATE_ASSET_DATA), and is no longer a page of its own — one
              command page targets a single asset or many. Redirected rather
              than dropped: the fleet
              map's popups, the Live/History deep links and anything a person
              bookmarked all point at the old path, and the SPA catch-all
              answers an unknown path with index.html — so a dead link here
              would look like a BLANK PAGE, not a 404. The query string is
              carried, which is the whole address of the asset. */}
          <Route path="/asset-data/control"
            element={<Navigate to={`/fleet/commands${window.location.search}`} replace />} />
          <Route path="/registry/*" element={<RegistryModule />} />
          <Route path="/drivers/*" element={<DriversModule />} />
          <Route path="/organisations/*" element={<OrganisationsModule />} />
          <Route path="/products/*" element={<ProductsModule />} />
          <Route path="/asset-catalog/*" element={<AssetCatalogModule />} />
          <Route path="/api/*" element={<ApiModule />} />
          <Route path="/link/*" element={<LinkModule />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Routes>
    </ScopeProvider>
  );
}
